import React, { useEffect, useState } from 'react';
import { Alert, AppLayout, Box, ContentLayout, Header, SpaceBetween, Spinner, Tabs, TabsProps } from '@amzn/awsui-components-core';
import { DashboardConfig, fetchDashboardConfigs } from 'src/services/DashboardsService';
import { OESideNavigation } from 'src/components/fintech-ops/fintech-ops-dashboard/OESideNavigation';
import { DashboardTabContent } from './DashboardTabContent';

/** KPI Metrics page — dynamically loads dashboards from DynamoDB registry */
export const KpiMetricsPage: React.FC = () => {
  const [dashboards, setDashboards] = useState<DashboardConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTabId, setActiveTabId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const configs = await fetchDashboardConfigs();
        if (cancelled) return;
        const aggregated = configs.filter((d) => d.dashboardId.startsWith('aggregated'));
        setDashboards(aggregated);
        if (aggregated.length > 0) setActiveTabId(aggregated[0].dashboardId);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppLayout
      navigation={<OESideNavigation />}
      toolsHide={true}
      maxContentWidth={Number.MAX_VALUE}
      content={
        <ContentLayout>
          <SpaceBetween size="m">
            <Header variant="h1" description="Embedded CloudWatch dashboards for monitoring and analytics across DaS Fintech portals">
              KPI Metrics
            </Header>

            {loading ? (
              <Box textAlign="center" padding="xxl">
                <Spinner size="large" />
              </Box>
            ) : error ? (
              <Alert type="error" header="Failed to load dashboards">
                An error occurred while loading dashboard configurations. Try refreshing the page.
              </Alert>
            ) : dashboards.length === 0 ? (
              <Box textAlign="center" padding="xxl">
                No dashboards configured.
              </Box>
            ) : (
              <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }: { detail: TabsProps.ChangeDetail }) => setActiveTabId(detail.activeTabId)}
                tabs={dashboards.map((d) => ({
                  id: d.dashboardId,
                  label: d.displayName,
                  content: <DashboardTabContent dashboardId={d.dashboardId} dashboardName={d.dashboardName} displayName={d.displayName} />
                }))}
              />
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};

export default KpiMetricsPage;
